import React, { useState } from "react";
import PerfOverlay, { PERF_OVERLAY } from "./PerfOverlay";

/**
 * PerfToggle - PerfOverlay behind a button, for the client (phone).
 *
 * The host is a TV with a whole screen to spare and nobody touching it, so it
 * gets the overlay permanently. A phone is the opposite: the player is holding
 * it and using every part of the screen, so a fixed panel is in the way.
 *
 *     import PerfToggle from "components/Shared/PerfOverlay/PerfToggle";
 *     <PerfToggle />
 *
 * Gated by the same PERF_OVERLAY flag as the overlay itself, so one line still
 * turns the whole thing off for a release build.
 *
 * MOUNTS ON OPEN, rather than rendering hidden. PerfOverlay samples every 2s
 * and does a getComputedStyle sweep over every element every 10s; that is fine
 * on an idle TV and not something to leave running unseen on a phone the player
 * is using. Closed, this costs a button and nothing else.
 *
 * That last sentence is only true because the profiler is reference counted
 * (see PerfOverlay.start/release). Unmounting the overlay used to leave the
 * singleton sampling for the rest of the session with nothing on screen, which
 * made this button look like it was saving the phone work while it was not.
 * If you change either file, keep the "actually stops sampling when closed"
 * test honest - checking that the panel has gone from the DOM does not prove it.
 *
 * Measurements survive a close, so drift resumes on reopen rather than
 * restarting. It is still a fit over a rolling window, so it needs a few
 * minutes of samples before it means anything.
 */

const BUTTON_STYLE = {
    position: "fixed",
    // Bottom-RIGHT: the overlay itself is bottom-left, so an open panel and its
    // own button never sit on top of each other.
    right: "calc(8px + env(safe-area-inset-right, 0px))",
    bottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
    zIndex: 10001,          // above PerfOverlay, so it stays tappable when open
    minWidth: 52,
    minHeight: 34,
    padding: "6px 10px",
    border: "1px solid rgba(255, 217, 61, 0.55)",
    borderRadius: 6,
    background: "rgba(8, 10, 18, 0.72)",
    color: "#ffd93d",
    font: "bold 12px monospace",
    letterSpacing: 1,
    lineHeight: 1,
    // Faint until wanted, so it reads as instrumentation rather than as part of
    // the game, and never competes with a real button for attention.
    opacity: 0.45,
    WebkitTapHighlightColor: "transparent",
};

export default function PerfToggle() {
    const [open, setOpen] = useState(false);

    if (!PERF_OVERLAY) return null;

    return (
        <>
            <button
                type="button"
                style={{ ...BUTTON_STYLE, opacity: open ? 0.95 : BUTTON_STYLE.opacity }}
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                aria-label={open ? "Hide performance overlay" : "Show performance overlay"}
            >
                {open ? "CLOSE" : "PERF"}
            </button>
            {open && <PerfOverlay />}
        </>
    );
}
